import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import makeStyles from "../../makeStylesV4Compat";
import clsx from "clsx";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AssembleTypography } from "../../components/AssembleTypography";
import { DeleteButton } from "../../components/Form/DeleteButton";
import { SaveButton } from "../../components/Form/SaveButton";
import {
  useCreateDepartment,
  useDeleteDepartment,
  useUpdateDepartment,
} from "../../mutations/Department";
import { GRAY_5 } from "../../theme";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3) },
  title: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(3),
  },
  context: {
    display: "flex",
    flexDirection: "column",
    rowGap: theme.spacing(3),
  },
  button: { height: theme.spacing(5) },
  cancelButton: { width: theme.spacing(9.375), borderColor: GRAY_5 },
}));

type DepartmentData = { id: number; name: string };

type Props = {
  mode: "create" | "edit";
  persistedDepartment?: DepartmentData;
  onCancel: () => unknown;
};

export function DepartmentForm({
  mode,
  persistedDepartment,
  onCancel,
}: Props): JSX.Element {
  const classes = useStyles();
  const navigate = useNavigate();
  const createDepartment = useCreateDepartment();
  const updateDepartment = useUpdateDepartment();
  const deleteDepartment = useDeleteDepartment();

  const [formState, setFormState] = useState({
    name: persistedDepartment?.name ?? "",
  });

  async function handleSubmit() {
    if (mode === "create") {
      await createDepartment({
        name: formState.name,
      });
    } else if (persistedDepartment) {
      await updateDepartment({
        id: persistedDepartment.id,
        name: formState.name,
      });
    }

    return true;
  }

  async function handleDelete() {
    if (persistedDepartment) {
      await deleteDepartment(persistedDepartment.id);
      navigate("/");
    }
  }

  return (
    <Dialog className={classes.root} open onClose={onCancel} fullWidth>
      <DialogTitle id="form-dialog-title" className={classes.title}>
        <AssembleTypography variant="h4">
          {mode === "create" ? "Create" : "Edit"} Department
        </AssembleTypography>
      </DialogTitle>
      <DialogContent className={classes.context}>
        <TextField
          autoFocus
          fullWidth
          id="department-name"
          label="Department Name"
          variant="outlined"
          value={formState.name}
          onChange={(e) => setFormState({ ...formState, name: e.target.value })}
          required
        />
      </DialogContent>
      <DialogActions>
        {mode === "edit" && (
          <>
            <DeleteButton
              buttonType="button"
              objectName="Department"
              onDelete={handleDelete}
              size="small"
            />
            <div style={{ flex: 1 }}></div>
          </>
        )}

        <Button
          className={clsx(classes.button, classes.cancelButton)}
          onClick={onCancel}
          variant="outlined"
          size="large"
        >
          <AssembleTypography variant="productSmallBold">
            Cancel
          </AssembleTypography>
        </Button>
        <SaveButton
          className={classes.button}
          onSave={handleSubmit}
          onAfterSave={onCancel}
          cooldown={500}
          hideEndIcon
        />
      </DialogActions>
    </Dialog>
  );
}
